import {Injectable} from '@angular/core';
import { HttpClient, HttpParams } from "@angular/common/http";
import {ContractsReport, EmployeesReport} from "@models/reports.model";
import {map, Observable} from "rxjs";
import {buildPath} from "@utils/util-builder";
import {ApiResponse} from "@models/api.response";
import {MatPaginator} from "@angular/material/paginator";
import {MatSort} from "@angular/material/sort";
import {matPaginatorToHttpParams, Page, paginatorToHttpParams} from "@utils/paginator-util";

@Injectable({
    providedIn: 'root'
})
export class ReportsService {

    private readonly _baseUrl: string = 'data-core/reports';

    constructor(private readonly _httpClient: HttpClient) {
    }

    getContracts(
        contracteeId: string,
        contractStartYear: string,
        paginator: MatPaginator,
        sort: MatSort
    ): Observable<Page<ContractsReport[]>> {
        const url: string = buildPath([this._baseUrl, 'contracts']);
        const params: HttpParams = paginatorToHttpParams(paginator, undefined, sort).appendAll({
            contracteeId,
            contractStartYear
        })

        return this._httpClient.get<ApiResponse<Page<ContractsReport[]>>>(url, {params}).pipe(
            map((response: ApiResponse<Page<ContractsReport[]>>): Page<ContractsReport[]> => response.data)
        )
    }

    getContractsXlsxFile(
        contracteeId: string,
        contractStartYear: string,
        paginator: MatPaginator,
        sort: MatSort
    ): Observable<Blob> {
        const url: string = buildPath([this._baseUrl, 'contracts', 'excel']);
        const params: HttpParams = paginatorToHttpParams(paginator, undefined, sort).appendAll({
            contracteeId,
            contractStartYear
        })

        return this._httpClient.get(url, {params, responseType: 'blob'});
    }

    getWorkers(
        contractorIds: string[],
        paymentYear: string,
        paginator: MatPaginator,
        sort: MatSort
    ): Observable<Page<EmployeesReport[]>> {
        const url: string = buildPath([this._baseUrl, 'employees']);
        let params: HttpParams = matPaginatorToHttpParams(paginator, undefined, sort);

        params = params.append('contractorIds', contractorIds.join(','))
        if (paymentYear) params = params.append('paymentYear', paymentYear);

        return this._httpClient.get<ApiResponse<Page<EmployeesReport[]>>>(url, {params}).pipe(
            map((response: ApiResponse<Page<EmployeesReport[]>>): Page<EmployeesReport[]> => response.data)
        )
    }

    getWorkersXlsxFile(
        contractorIds: string[],
        paymentYear: string,
        paginator: MatPaginator,
        sort: MatSort
    ): Observable<Blob> {
        const url: string = buildPath([this._baseUrl, 'employees', 'excel']);
        let params: HttpParams = new HttpParams();

        if (paginator && sort) params = matPaginatorToHttpParams(paginator, undefined, sort);
        else params = params.set('unpaged', true)

        params = params.append('contractorIds', contractorIds.join(','))
        if (paymentYear) params = params.append('paymentYear', paymentYear);

        return this._httpClient.get(url, {params, responseType: 'blob'});
    }

    getContractorAnnuallyReportFile(contractorId: string, year: string): Observable<Blob> {
        const url: string = buildPath([this._baseUrl, 'contractors/annually/excel']);
        const toParams: any = contractorId ? {contractorId, year} : {year};
        const params: HttpParams = new HttpParams().appendAll(toParams);
        return this._httpClient.get(url, {params, responseType: 'blob'});
    }

    getContractorMonthlyReportFile(contractorId: string, period: string): Observable<Blob> {
        const url: string = buildPath([this._baseUrl, 'contractors/monthly/excel']);
        const toParams: any = contractorId ? {contractorId, period} : {period};
        const params: HttpParams = new HttpParams().appendAll(toParams);
        return this._httpClient.get(url, {params, responseType: 'blob'});
    }

    getEvidencesAnnuallyReportFile(contractImssRegistryId: string, year: string): Observable<Blob> {
        const url: string = buildPath([this._baseUrl, 'evidences/annually/excel']);
        const toParams: any = {contractImssRegistryId, year};
        const params: HttpParams = new HttpParams().appendAll(toParams);
        return this._httpClient.get(url, {params, responseType: 'blob'});
    }

    getContracteeMonthlyReportFile(contracteeId: string, period: string): Observable<Blob> {
        const url: string = buildPath([this._baseUrl, 'contractees/monthly/excel']);
        const toParams: any = contracteeId ? {contracteeId, period} : {period};
        const params: HttpParams = new HttpParams().appendAll(toParams);
        return this._httpClient.get(url, {params, responseType: 'blob'});
    }

    nowStamp(): string {
        const d: Date = new Date();
        const pad = (n: number) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}`;
    }
}
